import React, { useState } from 'react';
import { Search, Heart, Brain, Sparkles, Book, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { teachings } from '../data/mock';

const Teachings = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Core Teachings', 'Meditation', 'Practice', 'Ethics'];

  const filteredTeachings = teachings.filter((teaching) => {
    const matchesSearch =
      teaching.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      teaching.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || teaching.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-[#ede1c5] text-stone-900">
      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1631949136465-af801b6c5244)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#ede1c5]/85 via-[#ede1c5]/55 to-[#ede1c5]" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-amber-400/12 blur-[140px]" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 text-amber-700/90 tracking-[0.3em] text-[11px] uppercase mb-6">
            <span className="h-px w-12 bg-amber-600/60" />
            <Sparkles size={14} />
            <span>Dhamma · Wisdom · Practice</span>
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] text-stone-900 max-w-3xl">
            Buddhist<br /><span className="italic text-amber-800">teachings.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-stone-700 leading-relaxed">
            Wisdom for daily life and spiritual growth — drawn from the Pāli Canon and 2,500 years of practice.
          </p>
        </div>
      </section>

      {/* SEARCH + FILTER */}
      <section className="py-12 border-t border-amber-500/40">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="relative mb-8 max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500" size={18} />
            <Input
              type="text"
              placeholder="Search teachings…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-6 text-base bg-[#fbf5e3]/85 border-stone-300 focus:border-amber-300 text-stone-900 placeholder:text-stone-500 rounded-full"
            />
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setSelectedCategory(c)}
                className={`px-5 py-2 rounded-full text-xs tracking-[0.15em] uppercase transition-all ${
                  selectedCategory === c
                    ? 'bg-amber-600 text-black'
                    : 'bg-[#fbf5e3]/90 text-stone-700 border border-stone-300 hover:border-amber-600/60 hover:text-amber-800'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* TEACHINGS GRID */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          {filteredTeachings.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredTeachings.map((t) => (
                <div
                  key={t.id}
                  className="group p-7 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/80 hover:bg-[#fbf5e3]/95 hover:border-amber-500/40 transition-all hover:-translate-y-1"
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-amber-700/80 bg-amber-100/60 px-3 py-1 rounded-full border border-amber-500/30">
                      {t.category}
                    </span>
                    {t.category === 'Core Teachings' && <Sparkles className="text-amber-700/70" size={16} />}
                    {t.category === 'Meditation' && <Brain className="text-amber-700/70" size={16} />}
                    {t.category === 'Practice' && <Heart className="text-amber-700/70" size={16} />}
                  </div>
                  <h3 className="font-serif-display text-xl text-stone-900 mb-3 leading-tight">{t.title}</h3>
                  <p className="text-stone-700 text-sm mb-3 leading-relaxed">{t.description}</p>
                  <p className="text-stone-500 text-xs leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Book className="mx-auto text-stone-400 mb-4" size={48} />
              <p className="text-stone-500">No teachings found matching your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* QUOTE */}
      <section className="py-24 bg-[#e3d4af] border-t border-amber-500/40">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-serif-display italic text-3xl md:text-4xl text-stone-900 leading-tight">
            "The mind is everything.<br />
            <span className="text-amber-800">What you think you become."</span>
          </p>
          <div className="mt-8 flex items-center justify-center gap-4 text-amber-800 text-sm tracking-[0.3em] uppercase">
            <span className="h-px w-10 bg-amber-600/60" /> The Buddha <span className="h-px w-10 bg-amber-600/60" />
          </div>
        </div>
      </section>

      {/* RESOURCES */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              Continue
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">
              Learning resources
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-8 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/80 hover:border-amber-500/40 transition-all">
              <h3 className="font-serif-display text-2xl text-stone-900 mb-4">Weekly Dhamma Talks</h3>
              <p className="text-stone-600 leading-relaxed">
                In-depth discussions on Buddhist teachings and their application to modern life.
              </p>
            </div>
            <div className="p-8 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/80 hover:border-amber-500/40 transition-all">
              <h3 className="font-serif-display text-2xl text-stone-900 mb-4">Meditation Instruction</h3>
              <p className="text-stone-600 leading-relaxed">
                Learn proper meditation techniques and receive personalized guidance.
              </p>
            </div>
          </div>
          <div className="text-center mt-12">
            <a
              href="/programs"
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-600 text-black px-7 py-3.5 rounded-full text-sm tracking-wide"
            >
              View All Programs <ArrowRight size={15} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Teachings;
